/**
 * Vehicle Form Page Component
 * /admin/vehicles/new and /admin/vehicles/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const VehicleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [investors, setInvestors] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [formData, setFormData] = useState({
    plate_number: '',
    make: '',
    model: '',
    year: '',
    vehicle_type: '',
    color: '',
    vin: '',
    status: 'active',
    ownership_type: '',
    owner_investor: '',
    owner_driver: '',
    purchase_date: '',
    purchase_amount: '',
  });

  useEffect(() => {
    if (isEdit) {
      loadVehicle();
    }
    loadInvestors();
    loadDrivers();
  }, [id]);

  const loadVehicle = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getVehicle(id);
      setFormData({
        plate_number: data.plate_number || '',
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        vehicle_type: data.vehicle_type || '',
        color: data.color || '',
        vin: data.vin || '',
        status: data.status || 'active',
        ownership_type: data.ownership_type || '',
        owner_investor: data.owner_investor || '',
        owner_driver: data.owner_driver || '',
        purchase_date: data.purchase_date || '',
        purchase_amount: data.purchase_amount || '',
      });
    } catch (err) {
      setError(err.message || 'Failed to load vehicle');
    } finally {
      setLoading(false);
    }
  };

  const loadInvestors = async () => {
    try {
      const response = await apiClient.getInvestors();
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setInvestors(data);
    } catch (err) {
      console.error('Failed to load investors:', err);
    }
  };

  const loadDrivers = async () => {
    try {
      // Note: You may need to add a getDrivers method to the API client
      // For now, we'll leave this empty or use a placeholder
      setDrivers([]);
    } catch (err) {
      console.error('Failed to load drivers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear owner fields when ownership type changes
    if (name === 'ownership_type') {
      setFormData((prev) => ({
        ...prev,
        owner_investor: '',
        owner_driver: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        purchase_amount: formData.purchase_amount ? parseFloat(formData.purchase_amount) : null,
        owner_investor: formData.ownership_type === 'investor' ? formData.owner_investor : null,
        owner_driver: formData.ownership_type === 'driver' ? formData.owner_driver : null,
      };

      if (isEdit) {
        await apiClient.updateVehicle(id, submitData);
      } else {
        await apiClient.createVehicle(submitData);
      }

      navigate('/admin/vehicles');
    } catch (err) {
      setError(err.message || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/vehicles')}>
            Back
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Plate Number"
                name="plate_number"
                value={formData.plate_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Make"
                name="make"
                value={formData.make}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleChange}
                slotProps={{ htmlInput: { min: 1900, max: new Date().getFullYear() + 1 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Vehicle Type"
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Color"
                name="color"
                value={formData.color}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="VIN"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
            Ownership Information
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ownership Type"
                name="ownership_type"
                value={formData.ownership_type}
                onChange={handleChange}
                select
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="company">Company</MenuItem>
                <MenuItem value="investor">Investor</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
              </TextField>
            </Grid>
            {formData.ownership_type === 'investor' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Owner (Investor)"
                  name="owner_investor"
                  value={formData.owner_investor}
                  onChange={handleChange}
                  select
                >
                  <MenuItem value="">Select Investor</MenuItem>
                  {investors.map((investor) => (
                    <MenuItem key={investor.id} value={investor.id}>
                      {investor.first_name} {investor.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {formData.ownership_type === 'driver' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Owner (Driver)"
                  name="owner_driver"
                  value={formData.owner_driver}
                  onChange={handleChange}
                  select
                >
                  <MenuItem value="">Select Driver</MenuItem>
                  {drivers.map((driver) => (
                    <MenuItem key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {formData.ownership_type && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Purchase Date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Purchase Amount (AED)"
                    name="purchase_amount"
                    type="number"
                    value={formData.purchase_amount}
                    onChange={handleChange}
                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/vehicles')}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default VehicleForm;

